import { FC, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { ExternalLink } from "lucide-react"
import { openInNewTab } from "../../common/utils/downloadFile"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import LeadDetailsHeader from "../../common/ui/leads/details/header"
import LeadActivityPage from "../../common/ui/leads/details/sections/activity"
import LeadQuotesPage from "../../common/ui/leads/details/sections/quotes"
import Tabs from "../../common/ui/tabs"
import { RequireRole } from "../../common/validation"
import {
  useFetchLeadDetailsQuery,
  useFetchLeadLogByIdQuery,
  useFetchSubscriptionsByLeadIdQuery,
  useUpdateSubscriptionMutation
} from "../../redux/queries"
import { SubscriptionDetails } from "../../types"
import { convertSubscriptionDetailsToPayload } from "../../common/utils"
import PushToEngagements from "../../common/ui/leads/push-to-engagements"
import LeadSummary from "../../common/ui/leads/details/sections/summary"
import { If, LinkBtn, SubscriptionDetailsSkeleton } from "../../common/ui"

const LeadDetailsPage: FC<{}> = () => {
  const { id } = useParams()

  const { data, isFetching: isFetchingLeadDetails } = useFetchLeadDetailsQuery(
    id ?? "",
    { skip: !id }
  )
  const { data: leadLog, isFetching: isFetchingLeadLog } =
    useFetchLeadLogByIdQuery(id ?? "", { skip: !id })

  const {
    data: subscriptions,
    refetch,
    isFetching: isFetchingSubscriptions
  } = useFetchSubscriptionsByLeadIdQuery(
    {
      lead_id: data?.id ?? ""
    },
    {
      skip: !data?.id
    }
  )

  const [updateSubscription] = useUpdateSubscriptionMutation()

  const [tab, setTab] = useState<string>("summary") // "summary", "quotes" or "activity"

  const isFetching =
    isFetchingLeadDetails || isFetchingLeadLog || isFetchingSubscriptions

  const [createEngagementVisible, setCreateEngagementVisible] =
    useState<boolean>(false)

  const handleCreateEngagement = () => {
    setCreateEngagementVisible(true)
  }

  const handleViewInEngagement = () => {
    if (subscriptions?.length) {
      openInNewTab(`/engagements/${subscriptions[0]?.id}`)
    }
  }

  const cancelCreateEngagement = () => setCreateEngagementVisible(false)

  const handleEnrollToRateAlerts = (subscription?: SubscriptionDetails) => {
    if (!subscription) return

    updateSubscription({
      ...convertSubscriptionDetailsToPayload(subscription),
      id: subscription.id,
      active: subscription.active === "1" ? "0" : "1"
    })
  }

  useEffect(() => {
    if (data?.id && refetch) {
      refetch()
    }
  }, [data?.id, refetch])

  if (!data) return null

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE["engagements"]}>
      <If
        condition={isFetching}
        then={<SubscriptionDetailsSkeleton />}
        else={
          <>
            <div className="h-full flex flex-col">
              <div className="pt-6 bg-white">
                <div className="px-6">
                  <LeadDetailsHeader
                    data={data}
                    bankName={leadLog?.brand_name}
                    subscription={subscriptions?.[0]}
                    onCreateEngagement={handleCreateEngagement}
                    onEnrollToRateAlerts={handleEnrollToRateAlerts}
                  />
                </div>
                <Tabs
                  className="px-6"
                  currentTab={tab}
                  tabs={[
                    { id: "summary", label: "Summary" },
                    { id: "quotes", label: "Quotes" },
                    { id: "activity", label: "Embed Activity" }
                  ]}
                  onChange={setTab}
                  extraElement={
                    subscriptions?.length ? (
                      <LinkBtn
                        className="-my-1 -mx-4"
                        onClick={handleViewInEngagement}
                      >
                        <span className="mr-2">View in engagements</span>
                        <ExternalLink size={16} />
                      </LinkBtn>
                    ) : null
                  }
                />
              </div>
              <div className="p-6 flex-1 bg-white">
                {tab === "summary" && <LeadSummary data={data} />}
                {tab === "quotes" && <LeadQuotesPage data={data} />}
                {tab === "activity" && <LeadActivityPage data={data} />}
              </div>
            </div>

            <PushToEngagements
              brandId={leadLog?.group_id}
              leadId={createEngagementVisible ? data.id : ""}
              onCancel={cancelCreateEngagement}
            />
          </>
        }
      />
    </RequireRole>
  )
}

export default LeadDetailsPage
